import { useState, useEffect } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { useProfile } from "../../../hooks/useProfile";
import { BASE_URL } from "../utils/reportsHelpers";

/**
 * Real, tenant-and-visibility-scoped option lists for the filter dropdowns
 * (Employee, Department, Company, Manager) and for the Share recipient
 * picker — replaces free-text inputs so the user only ever picks from what
 * they're actually allowed to see.
 *
 * Employees come from GET /api/employees/visibility, the same
 * admin-level-visibility-scoped endpoint the Messenger recipient picker and
 * other employee pickers already use across the app (it consults
 * AdminLevelVisibilityRule, not just clientName) — NOT the generic
 * GET /api/employees, which has no tenant filter at all.
 */
const useReportFilterOptions = (currentUser) => {
  const { profile, profileLoading } = useProfile();
  // getMyProfile() (GET /users/me) returns a flat shape — clientName is
  // profile.clientName directly, there's no nested `employee` object.
  const clientName = profile?.clientName || null;

  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Waiting on the profile fetch itself — keep showing "loading", it'll
    // resolve. But once the profile HAS loaded and there's still no
    // clientName/currentUser to work with, there's nothing left to wait
    // for — stop spinning forever and just show empty option lists.
    if (!currentUser || !clientName) {
      if (!profileLoading) setLoading(false);
      return;
    }
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [deptRes, empRes, companyRes] = await Promise.all([
          apiFetch(`${BASE_URL}/api/departments?clientName=${encodeURIComponent(clientName)}`, { method: "GET" }).catch(() => null),
          apiFetch(`${BASE_URL}/api/employees/visibility?clientName=${encodeURIComponent(clientName)}`, { method: "GET" }).catch(() => null),
          apiFetch(`${BASE_URL}/api/company-config/${encodeURIComponent(clientName)}/detect-companies`, { method: "GET" }).catch(() => null),
        ]);

        if (cancelled) return;

        if (deptRes?.ok) {
          const json = await deptRes.json();
          setDepartments((json.data || []).map((d) => ({ key: d.name, label: d.name })));
        }

        if (empRes?.ok) {
          const json = await empRes.json();
          const rows = json.employees || [];
          setEmployees(rows.map((e) => ({
            key: e.infinityCode,
            label: `${e.sr || e.arabicName || e.infinityCode} (${e.infinityCode})`,
            email: e.email || null,
          })));
        }

        if (companyRes?.ok) {
          const json = await companyRes.json();
          const list = json.data?.companies || json.companies || [];
          setCompanies(list.map((c) => (typeof c === "string" ? { key: c, label: c } : { key: c.companyName, label: c.companyName })).filter((c) => c.key));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [currentUser, clientName, profileLoading]);

  return {
    departments,
    employees,
    companies,
    managers: employees, // any visible employee can be searched as a manager, same source
    optionsLoading: loading,
  };
};

export default useReportFilterOptions;
